// novem atom — view-mode toggle (NovemHR-ekranlar port, Phase 8.6 promotion).
//
// The grid/list segmented toggle from the directory toolbar
// (emp-directory.jsx): a row of icon buttons inside a tinted track, one
// pressed. Promoted out of employee-list-content.tsx's DirectoryView where
// it was hand-rolled. Generic over the option set — the directory passes
// grid/list with LayoutGrid/Rows3 icons, but any small mutually-exclusive
// view-mode set works.
//
// Icons are caller-supplied ReactNode (same stance as StatCard.icon) so the
// library keeps no icon-pack dependency. Each option carries an accessible
// label (the visible content is icon-only, so the label is required).
//
// Accessibility (toggle-button group, NOT a radiogroup): the original
// markup used role="group" + aria-pressed buttons — a set of independent
// toggle buttons where exactly one is pressed at a time. Preserved verbatim
// so behavior + a11y stay byte-identical:
//   * Container role="group" + aria-label names the set.
//   * Each button is a real <button> (native Tab/Enter/Space) with
//     aria-pressed reflecting selection and aria-label naming the option.
// (We deliberately do NOT upgrade to radiogroup/roving-tabindex here —
// that would change the keyboard model the screen already shipped.)

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface ViewToggleOption<V extends string = string> {
  readonly value: V;
  /** Icon element shown in the button (icon-only — label carries the name). */
  readonly icon: ReactNode;
  /** Accessible name for the option (required — the button is icon-only). */
  readonly label: string;
}

export interface ViewToggleProps<V extends string = string>
  extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  readonly options: ReadonlyArray<ViewToggleOption<V>>;
  readonly value: V;
  readonly onChange: (value: V) => void;
  /** Accessible name for the toggle group (required — no visible legend). */
  readonly "aria-label": string;
}

function ViewToggleInner<V extends string = string>(
  { options, value, onChange, "aria-label": ariaLabel, className, ...rest }: ViewToggleProps<V>,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.ReactElement {
  return (
    <div
      ref={ref}
      role="group"
      aria-label={ariaLabel}
      data-component="view-toggle"
      className={cx("nai-view-toggle", className)}
      {...rest}
    >
      {options.map((option) => {
        const pressed = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            aria-label={option.label}
            data-pressed={pressed || undefined}
            className="nai-view-toggle-btn"
            onClick={(): void => onChange(option.value)}
          >
            {option.icon}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Generic forwardRef wrapper. The cast preserves the `<V>` value-union
 * generic through forwardRef (which otherwise erases it) so call sites
 * keep narrow typing on value/onChange (same pattern as FilterPillBar).
 */
export const ViewToggle = forwardRef(ViewToggleInner) as <V extends string = string>(
  props: ViewToggleProps<V> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
